import { motion } from 'framer-motion';
import { Link, useLocation } from 'react-router-dom';
import { useEffect } from 'react';
import { Building2, Battery, Sun, Zap, ArrowRight, CheckCircle, Clock, FileText, Handshake, Settings, ChevronRight } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { ResponsiveImage } from '../components/ResponsiveImage';
import { getRatgeberLinksForService } from '../data/seoTopicClusters';

const services = [
  {
    id: 'schwarmspeicher',
    icon: Zap,
    title: 'Kleinspeicher / Schwarmspeicher',
    subtitle: 'Speicherstandorte ab 5m²',
    heroImage: '/assets/kleinspeicher-schwarmspeicher-2026-06-01.jpeg',
    heroImageWidth: 1220,
    heroImageHeight: 1102,
    description: 'Schwarmspeicher erschließen kleine Flächen für dezentrale Speicherlösungen. Geeignet sind vor allem Gewerbe-, Industrie- und Mischgebiete, teilweise auch Wohngebiete. Je nach Standort kann das Modell auf bis zu 20m² skaliert werden.',
    benefits: [
      'Einmalzahlung bis zu 25.000€ je 5m² möglich',
      'Alternativ jährlich bis zu 1.600€ je 5m² möglich',
      'Optional skalierbar bis 20m²',
      'Geeignet für Gewerbe, Industrie, Mischgebiet und teilweise Wohngebiet',
      'Laufzeit bis zu 30 Jahre',
      'Keine eigene Investition erforderlich',
      'Planung, Betrieb und Monitoring aus einer Hand'
    ],
    specs: [
      { label: 'Mindestfläche', value: '5 m²' },
      { label: 'Einmalig', value: 'bis zu 25.000€ je 5m²' },
      { label: 'Jährlich', value: 'bis zu 1.600€ je 5m²' },
      { label: 'Investition', value: '0 €' },
    ],
    process: [
      { step: 'Standortanalyse', desc: 'Prüfung von Fläche, Lage und Netzpotenzial' },
      { step: 'Konzept', desc: 'Auslegung des Speicherstandorts und der Skalierung' },
      { step: 'Angebot', desc: 'Individuelles Modell für Ihre Fläche' },
      { step: 'Vertrag', desc: 'Faire Konditionen und transparente Laufzeit' },
      { step: 'Installation', desc: 'Aufbau, Vernetzung und Betriebsstart' },
    ]
  },
  {
    id: 'batteriespeicher',
    icon: Battery,
    title: 'Batteriespeicher (BESS)',
    subtitle: 'Flächen ab 80m²',
    heroImage: '/assets/bess-grossspeicher-scalecube-i3-2026-06-01.png',
    heroImageWidth: 1920,
    heroImageHeight: 831,
    description: 'Batteriespeicher sind ein zentraler Baustein der Energiewende. Nähe zu einem Umspannwerk ist von Vorteil, aber nicht zwingend erforderlich. Bevorzugt sind Flächen in Gewerbe- und Industriegebieten; Mischgebiete und Freiflächen können ebenfalls geeignet sein.',
    benefits: [
      'Pachteinnahmen bis zu 30.000€ pro Jahr für 1.000m² möglich',
      'Minimaler Flächenbedarf',
      'Langfristige Verträge bis zu 30 Jahre',
      'Kein operatives Risiko für Sie',
      'Gewerbegebiete, Industriegebiete, Mischgebiete und Freiflächen möglich',
      'Nähe zu einem Umspannwerk vorteilhaft'
    ],
    specs: [
      { label: 'Mindestfläche', value: '80 m²' },
      { label: 'Pachteinnahmen', value: 'bis zu 30.000€/Jahr je 1.000m²' },
      { label: 'Laufzeit', value: 'bis zu 30 Jahre' },
      { label: 'Investition', value: '0 €' },
    ],
    process: [
      { step: 'Standortanalyse', desc: 'Prüfung von Fläche, Lage und Netzanschlusspotenzial' },
      { step: 'Angebot', desc: 'Individuelles Pacht- und Nutzungskonzept' },
      { step: 'Vertrag', desc: 'Faire Konditionen und transparente Laufzeit' },
      { step: 'Genehmigung', desc: 'Netzanschlussprüfung und Baugenehmigungen' },
      { step: 'Installation', desc: 'Bau, Anschluss und Inbetriebnahme des Speichers' },
    ]
  },
  {
    id: 'freiflaechen',
    icon: Sun,
    title: 'Freiflächen-Photovoltaik',
    subtitle: 'Agrar- und Brachflächen ab 1 Hektar',
    heroImage: '/assets/freiflaechen-pv-800.jpg',
    heroImageWidth: 800,
    heroImageHeight: 500,
    description: 'Acker, Grünland, Brache sowie Gewerbe- und Industrieflächen können langfristig für Freiflächen-Photovoltaik entwickelt werden. Wir prüfen die Eignung, sichern die Fläche und strukturieren das Projekt über die gesamte Laufzeit.',
    benefits: [
      'Pachteinnahmen bis zu 5.000€/ha p.a.',
      'Bis zu 10x höher als Agrarpacht',
      'Langfristige Planungssicherheit bis zu 40 Jahre',
      'Doppelnutzung mit Agri-PV möglich',
      'Geeignet für Acker, Grünland, Brache, Gewerbe- und Industrieflächen',
      'Keine eigene Investition erforderlich'
    ],
    specs: [
      { label: 'Mindestfläche', value: '1 Hektar' },
      { label: 'Pachteinnahmen', value: 'bis zu 5.000€/ha p.a.' },
      { label: 'Laufzeit', value: 'bis zu 40 Jahre' },
      { label: 'Investition', value: '0 €' },
    ],
    process: [
      { step: 'Standortanalyse', desc: 'Analyse von Lage, Größe und Netzanschlusspotenzial' },
      { step: 'Angebot', desc: 'Individuelles Pachtangebot für Ihre Fläche' },
      { step: 'Vertrag', desc: 'Faire und langfristige Pachtkonditionen' },
      { step: 'Genehmigung', desc: 'Netzanschlussprüfung und Baugenehmigungen' },
      { step: 'Installation', desc: 'Bau, Netzanschluss und Inbetriebnahme der Anlage' },
    ]
  },
  {
    id: 'dachverpachtung',
    icon: Building2,
    title: 'Dachverpachtung',
    subtitle: 'Gewerbe- & Industriedächer ab 400m²',
    heroImage: '/assets/pv-dachflaeche-2026-06-01-193249.jpeg',
    heroImageWidth: 2048,
    heroImageHeight: 945,
    description: 'Verwandeln Sie Ihr ungenutztes Dach in eine zuverlässige Einnahmequelle. Wir pachten Ihre Dachfläche, installieren eine Photovoltaikanlage und übernehmen Kosten sowie Risiken. Je nach Dachzustand und Projektumfang kann auch eine Dachsanierung statt Pachtzahlung möglich sein.',
    benefits: [
      'Pachteinnahmen bis zu 40.000€ pro 1.000m² Modulfläche möglich',
      'Dachsanierung statt Pachtzahlung je nach Zustand und Projektumfang möglich',
      'Keine eigene Investition erforderlich',
      'Vertragslaufzeit bis zu 30 Jahre mit planbaren Konditionen',
      'Mögliche kostenfreie Übernahme der PV-Anlage nach Vertragslaufzeit',
      'Beitrag zum Klimaschutz'
    ],
    specs: [
      { label: 'Mindestfläche', value: '400 m²' },
      { label: 'Pachteinnahmen', value: 'bis zu 40.000€ je 1.000m² Modulfläche' },
      { label: 'Laufzeit', value: 'bis zu 30 Jahre' },
      { label: 'Investition', value: '0 €' },
    ],
    process: [
      { step: 'Erstgespräch', desc: 'Kostenlose Beratung und Bedarfsklärung' },
      { step: 'Angebot', desc: 'Individuelles Pacht- oder Sanierungsmodell' },
      { step: 'Vertrag', desc: 'Faire Konditionen und transparente Laufzeit' },
      { step: 'Technische Prüfung', desc: 'Netzanschlussprüfung, Dachinspektion und Statikprüfung' },
      { step: 'Installation', desc: 'Professionelle Montage und Inbetriebnahme' },
    ]
  },
];

const processIcons = [Clock, FileText, Handshake, Settings, CheckCircle];

export const Leistungen = () => {
  const location = useLocation();

  useEffect(() => {
    if (location.hash) {
      const element = document.getElementById(location.hash.slice(1));
      if (element) {
        setTimeout(() => {
          element.scrollIntoView({ behavior: 'smooth', block: 'start' });
        }, 100);
      }
    }
  }, [location]);

  return (
    <div className="min-h-screen" data-testid="leistungen-page">
      {/* Hero Section */}
      <section className="bg-[#122A5C] py-14 md:py-28">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div 
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
            className="text-center"
          >
            <span className="inline-block text-xs font-bold uppercase tracking-[0.2em] text-[#E07000] mb-4">
              Unsere Leistungen
            </span>
            <h1 className="font-heading mb-5 text-2xl font-bold text-white sm:text-3xl md:mb-6 md:text-4xl lg:text-5xl">
              Maßgeschneiderte Lösungen für Ihre Fläche
            </h1>
            <p className="mx-auto max-w-3xl text-base text-white/80 md:text-lg">
              Von Kleinspeichern über Batteriespeicher bis zur Freiflächen-Photovoltaik und Dachverpachtung –
              wir entwickeln das optimale Projekt für Ihre individuellen Anforderungen.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Quick Navigation */}
      <section className="sticky top-[53px] md:top-[70px] z-40 bg-white border-b border-slate-200 shadow-sm">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex overflow-x-auto py-4 gap-2 md:gap-4 scrollbar-hide">
            {services.map((service) => (
              <a
                key={service.id}
                href={`#${service.id}`}
                className="flex items-center gap-2 px-4 py-2 rounded-full bg-slate-100 hover:bg-[#E07000] hover:text-white transition-colors whitespace-nowrap text-sm font-medium"
                data-testid={`nav-${service.id}`}
              >
                <service.icon className="w-4 h-4" />
                {service.title}
              </a>
            ))}
          </div>
        </div>
      </section>

      {/* Service Sections */}
      {services.map((service, index) => (
        <section 
          key={service.id}
          id={service.id}
          className={`py-16 md:py-28 ${index % 2 === 0 ? 'bg-white' : 'bg-slate-50'}`}
          data-testid={`service-section-${service.id}`}
        >
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            {/* Service Header */}
            <div className={`mb-12 grid items-center gap-8 md:mb-16 lg:grid-cols-2 lg:gap-12 ${index % 2 === 1 ? 'lg:flex-row-reverse' : ''}`}>
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5 }}
                viewport={{ once: true }}
                className={index % 2 === 1 ? 'lg:order-2' : ''}
              >
                <div className="flex items-center gap-3 mb-4">
                  <div className="w-12 h-12 bg-[#E07000]/10 rounded-xl flex items-center justify-center">
                    <service.icon className="w-6 h-6 text-[#E07000]" />
                  </div>
                  <span className="text-sm font-medium text-[#E07000]">{service.subtitle}</span>
                </div>
                <h2 className="font-heading mb-4 text-2xl font-bold text-[#122A5C] md:text-4xl">
                  {service.title}
                </h2>
                <p className="mb-6 text-base leading-relaxed text-slate-600 md:text-lg">
                  {service.description}
                </p>
                <Link to="/angebot-anfordern">
                  <Button 
                    className="bg-[#E07000] hover:bg-[#C56200] text-white rounded-full px-6 py-3 font-semibold btn-animate"
                    data-testid={`cta-${service.id}`}
                  >
                    Kostenlos beraten lassen
                    <ArrowRight className="ml-2 w-4 h-4" />
                  </Button>
                </Link>
                {getRatgeberLinksForService(service.id, 3).length ? (
                  <div className="mt-6 border-t border-slate-200 pt-5">
                    <p className="mb-3 text-xs font-bold uppercase tracking-[0.16em] text-slate-500">
                      Passende Ratgeber
                    </p>
                    <div className="space-y-2">
                      {getRatgeberLinksForService(service.id, 3).map((article) => (
                        <Link
                          key={article.slug}
                          to={`/ratgeber/${article.slug}`}
                          className="group flex items-center justify-between gap-3 rounded-md px-0 py-1 text-sm font-semibold text-[#122A5C] transition-colors hover:text-[#E07000]"
                        >
                          <span>{article.title}</span>
                          <ArrowRight className="h-4 w-4 flex-shrink-0 transition-transform group-hover:translate-x-1" />
                        </Link>
                      ))}
                    </div>
                  </div>
                ) : null}
              </motion.div>

              <motion.div
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5 }}
                viewport={{ once: true }}
                className={`relative ${index % 2 === 1 ? 'lg:order-1' : ''}`}
              >
                <div className="overflow-hidden rounded-lg shadow-xl md:rounded-2xl img-zoom">
                  <ResponsiveImage
                    src={service.heroImage}
                    alt={service.title}
                    width={service.heroImageWidth}
                    height={service.heroImageHeight}
                    sizes="(min-width: 1024px) 50vw, 100vw"
                    loading={index === 0 ? 'eager' : 'lazy'}
                    fetchPriority={index === 0 ? 'high' : 'auto'}
                    decoding="async"
                    className="h-[240px] w-full object-cover md:h-[400px]"
                  />
                </div>
              </motion.div>
            </div>

            {/* Benefits & Specs */}
            <div className="mb-12 grid gap-6 md:mb-16 md:grid-cols-2 md:gap-8">
              {/* Benefits */}
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5 }}
                viewport={{ once: true }}
              >
                <Card className="h-full rounded-lg border-0 shadow-lg md:rounded-2xl">
                  <CardContent className="p-6 md:p-8">
                    <h3 className="font-heading text-xl font-semibold text-[#122A5C] mb-6">
                      Ihre Vorteile
                    </h3>
                    <ul className="space-y-4">
                      {service.benefits.map((benefit, i) => (
                        <li key={i} className="flex items-start gap-3">
                          <CheckCircle className="w-5 h-5 text-green-500 flex-shrink-0 mt-0.5" />
                          <span className="text-slate-600">{benefit}</span>
                        </li>
                      ))}
                    </ul>
                  </CardContent>
                </Card>
              </motion.div>

              {/* Specs */}
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: 0.1 }}
                viewport={{ once: true }}
              >
                <Card className="h-full rounded-lg border-0 bg-[#122A5C] shadow-lg md:rounded-2xl">
                  <CardContent className="p-6 md:p-8">
                    <h3 className="font-heading text-xl font-semibold text-white mb-6">
                      Auf einen Blick
                    </h3>
                    <div className="grid grid-cols-1 gap-5 sm:grid-cols-2 md:gap-6">
                      {service.specs.map((spec, i) => (
                        <div key={i}>
                          <div className="text-white/60 text-sm mb-1">{spec.label}</div>
                          <div className="text-white font-semibold text-lg">{spec.value}</div>
                        </div>
                      ))}
                    </div>
                  </CardContent>
                </Card>
              </motion.div>
            </div>

            {/* Process Steps */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5 }}
              viewport={{ once: true }}
            >
              <h3 className="font-heading text-2xl font-semibold text-[#122A5C] text-center mb-8">
                Unser Prozess
              </h3>
              <div className="relative">
                {/* Connection Line */}
                <div className="hidden md:block absolute top-8 left-0 right-0 h-0.5 bg-gradient-to-r from-[#E07000] to-[#122A5C]" />
                
                <div className="grid grid-cols-1 gap-6 sm:grid-cols-2 md:grid-cols-5">
                  {service.process.map((step, i) => {
                    const Icon = processIcons[i];
                    return (
                      <div key={i} className="relative text-center">
                        <div className="w-16 h-16 bg-white rounded-full border-2 border-[#E07000] flex items-center justify-center mx-auto mb-4 relative z-10">
                          <Icon className="w-7 h-7 text-[#E07000]" />
                        </div>
                        <h4 className="font-semibold text-[#122A5C] mb-1">{step.step}</h4>
                        <p className="text-sm text-slate-500">{step.desc}</p>
                      </div>
                    );
                  })}
                </div>
              </div>
            </motion.div>
          </div>
        </section>
      ))}

      {/* Bottom CTA */}
      <section className="bg-[#E07000] py-16 md:py-20">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
            viewport={{ once: true }}
          >
            <h2 className="font-heading mb-5 text-3xl font-bold text-white md:mb-6 md:text-4xl">
              Welche Lösung passt zu Ihnen?
            </h2>
            <p className="mb-8 text-base text-white/90 md:text-xl">
              Lassen Sie uns gemeinsam herausfinden, wie Sie das Beste aus Ihrer Fläche herausholen können.
            </p>
            <Link to="/angebot-anfordern">
              <Button 
                className="bg-white text-[#E07000] hover:bg-slate-100 rounded-full px-8 py-4 text-lg font-semibold btn-animate"
                data-testid="bottom-cta-button"
              >
                Kostenlose Beratung anfordern
                <ChevronRight className="ml-2 w-5 h-5" />
              </Button>
            </Link>
          </motion.div>
        </div>
      </section>
    </div>
  );
};
